import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  EventEmitter,
  ElementRef,
  Output,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormArray,
  UntypedFormControl,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import { Observable } from 'rxjs';
import * as _ from 'lodash';

import patterns from 'utils/patterns';
import { DefaultModalComponent } from '../default-modal/default-modal.component';
import { BaseModalComponent } from '../_base-modal.component';
import { CreateNewProductService } from './create-new-product.service';
import { BrandService } from '../../brand/brand.service';
import {of} from 'rxjs/internal/observable/of';
import {map} from 'rxjs/operators';

@Component({
  moduleId: module.id,
  selector: 'create-new-product-modal',
  templateUrl: 'create-new-product-modal.component.html',
  styleUrls: ['create-new-product-modal.component.scss'],
  providers: [CreateNewProductService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateNewProductModalComponent extends BaseModalComponent implements OnInit {

  form: UntypedFormGroup;
  isFormScrolling: boolean = false;

  @Output() saveEvent: EventEmitter<any> = new EventEmitter();

  @Output() createClicked: EventEmitter<any> = new EventEmitter();

  @ViewChild('content', {static: false})
  private content: ElementRef | void;

  @ViewChild('formElement', {static: false})
  private formElement: ElementRef | void;

  @ViewChild('controlsList', {static: false})
  private controlsList: ElementRef | void;

  private controlHeight: number = 81;
  private addingControl: boolean = false;
  private removingControl: boolean = false;

  get partNumbers(): UntypedFormArray {
    return this.form.get('partNumbers') as UntypedFormArray;
  }

  get newControl(): UntypedFormControl {
    return new UntypedFormControl(
      '',
      [this.uniqueValidator().bind(this), Validators.required],
      [this.availabilityValidator().bind(this)],
    );
  }

  get disabled(): boolean {
    return this.form.pristine || this.form.invalid;
  }

  get formElementHeight(): number {
    return this.formElement && this.formElement.nativeElement.offsetHeight;
  }

  get controlsListHeight(): number {
    return this.controlsList && this.controlsList.nativeElement.offsetHeight;
  }

  get brandId(): any {
    return this.brandService.getBrand();
  }

  get availibalityError(): any {
    return {'partNumber': 'Unable to add the part number. It already exists in the working or archive catalog.'};
  }

  get uniqueError(): any {
    return {'unique': 'Part number is not unique.'};
  }

  constructor(
    private fb: UntypedFormBuilder,
    private createNewProductService: CreateNewProductService,
    private brandService: BrandService,
    private ref: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    this.form = this.fb.group({
      partNumbers: this.fb.array([])
    });
  }

  // public:
  public show() {
    super.show();
    this.form.setControl('partNumbers', new UntypedFormArray([this.newControl]));
  }

  // handlers:
  onCreateClick() {
    if (this.disabled) { return; }
    const data = _.map(this.partNumbers.value, (partNumber: any) => {
      return { partNumber };
    });
    this.createClicked.emit(data);
    this.hide();
  }

  onAddAnotherClick() {
    this.addingControl = true;
    this.partNumbers.push(this.newControl);
    _.defer(() => {
      this.isFormScrolling =
        this.content && this.content.nativeElement.offsetHeight >= (window.innerHeight - this.controlHeight);
      if (this.isFormScrolling && this.formElement) {
        this.formElement.nativeElement.scrollTop = this.controlsListHeight;
      }
      this.addingControl = false;
    });
  }

  onDeleteClick(index: number) {
    this.removingControl = true;
    this.partNumbers.removeAt(index);
    _.defer(() => {
      if (this.isFormScrolling) {
        this.isFormScrolling = !(this.formElementHeight > this.controlsListHeight);
      }
      this.removingControl = false;
    });
  }

  onKeydown(event: KeyboardEvent) {
    if (!patterns.ANpartNumber.test(event.key)) {
      event.preventDefault();
    }
  }

  // private
  private availabilityValidator(): AsyncValidatorFn {
    return (control: any): Observable<{[key: string]: any}> => {
      if (!control.value) { return of(null); }
      // do not do request while removing/adding controls
      if (this.removingControl || this.addingControl) {
        return control.isAvailable ? of(null) : of(this.availibalityError);
      }
      return this.createNewProductService.partNumberAvailability(
        this.brandId, control.value)
      .pipe(map(response => {
        // save availablity status in control
        control.isAvailable = response.available;
        this.ref.markForCheck();
        return response.available ? null : this.availibalityError;
      }));
    };
  }

  private uniqueValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} => {
      const hasIdentical = _.some(this.partNumbers.controls, (item: UntypedFormControl) => {
        return item !== control && item.valid && item.value === control.value;
      });
      this.ref.markForCheck();
      return hasIdentical ? this.uniqueError : null;
    };
  }

  private resetPartNumbers(): void {
    this.form.setControl('partNumbers', new UntypedFormArray([this.newControl]));
  }
}
